import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  readSwrCache,
  writeSwrCache,
  SWR_MAX_AGE_LONG,
} from "../../utils/swrCache";

// ----------------- SETTINGS -----------------
export const settings = {
  setStorageJson: (name, data) => {
    data = JSON.stringify(data);
    localStorage.setItem(name, data);
  },
  setStorage: (name, data) => {
    localStorage.setItem(name, data);
  },
  getStorageJson: (name) => {
    if (localStorage.getItem(name)) {
      const data = JSON.parse(localStorage.getItem(name));
      return data;
    }
    return;
  },

  getStore: (name) => {
    if (localStorage.getItem(name)) {
      const data = localStorage.getItem(name);
      return data;
    }
    return;
  },
  setCookieJson: (name, value, days) => {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    value = JSON.stringify(value);
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  },
  getCookieJson: (name) => {
    var nameEQ = name + "=";
    var ca = document.cookie.split(";");
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0)
        return JSON.parse(c.substring(nameEQ.length, c.length));
    }
    return null;
  },
  setCookie: (name, value, days) => {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  },
  delete_cookie: (name) => {
    document.cookie =
      name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  },
  getCookie: (name) => {
    var nameEQ = name + "=";
    var ca = document.cookie.split(";");
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },
  eraseCookie: (name) => {
    document.cookie =
      name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  },
};

// ----------------- AXIOS HTTP -----------------
export const http = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Interceptor để thêm token từ cookie vào mỗi request
http.interceptors.request.use((config) => {
  const token = settings.getCookie("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// (Tùy chọn) Xử lý lỗi 401 toàn cục
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Token hết hạn hoặc không hợp lệ.");
    }
    return Promise.reject(error);
  }
);

// ----------------- SWR CACHE -----------------
// Menu đổi rất ít nhưng lại nằm trên đường tới hạn của lần vẽ đầu: chưa có menu là
// sidebar trống. Cache theo phiên đăng nhập (xem utils/swrCache.js), vẽ ngay bản stale
// rồi revalidate nền.
const MENU_CACHE_KEY = "menu";
// Trong khoảng này không gọi lại API dù component remount.
const MENU_DEDUPE_INTERVAL = 30 * 1000;

// ----------------- ASYNC THUNK -----------------
// Truyền { force: true } sau khi sửa/sắp xếp menu để bỏ qua cửa sổ dedupe.
export const getMenuOfUser = createAsyncThunk(
  "Menu/getListMenuOfUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await http.get("/api/v1/security/user/menus");
      writeSwrCache(MENU_CACHE_KEY, response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue("Có lỗi xảy ra trong quá trình kết nối");
    }
  },
  {
    condition: (arg, { getState }) => {
      const state = getState()?.persistedReducer?.getListMenuUserSlice;
      if (!state) return true;
      // Đang có request bay: không gọi trùng.
      if (state.isValidating) return false;
      if (arg?.force) return true;
      if (!state.lastFetchedAt) return true;
      return Date.now() - state.lastFetchedAt > MENU_DEDUPE_INTERVAL;
    },
  }
);

// ----------------- SLICE -----------------
// Đọc cache ngay tại lúc tạo initialState (đồng bộ) để menu có mặt ở lần render đầu,
// không chờ redux-persist rehydrate hay chờ API.
// Menu là dữ liệu cấu hình, đổi rất ít -> dùng hạn dài. Khác với số liệu tiền/tiến độ
// (SWR_MAX_AGE_SHORT), menu cũ vài ngày vẫn gần như đúng.
const cachedMenu = readSwrCache(MENU_CACHE_KEY, { maxAge: SWR_MAX_AGE_LONG });

const initialState = {
  menuOfUser: cachedMenu ?? [],
  isLoading: false, // chỉ bật khi CHƯA có gì để vẽ
  isValidating: false, // đang revalidate nền
  isStale: !!cachedMenu, // dữ liệu đang vẽ đến từ cache
  lastFetchedAt: null, // null => lần dispatch đầu luôn được đi
};

const getListMenuUserSlice = createSlice({
  name: "getListMenuUser",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMenuOfUser.pending, (state) => {
        state.isValidating = true;
        // Đã có menu (cache hoặc lần fetch trước) thì không bật loading -> sidebar
        // không nháy trống trong lúc revalidate.
        state.isLoading = !state.menuOfUser?.data?.length;
      })
      .addCase(getMenuOfUser.fulfilled, (state, action) => {
        state.menuOfUser = action.payload;
        state.isLoading = false;
        state.isValidating = false;
        state.isStale = false;
        state.lastFetchedAt = Date.now();
      })
      .addCase(getMenuOfUser.rejected, (state) => {
        state.isLoading = false;
        state.isValidating = false;
        // Giữ nguyên menuOfUser: revalidate lỗi thì vẫn dùng bản stale thay vì
        // làm trống sidebar. lastFetchedAt vẫn null nên lần sau còn thử lại.
      });
  },
});

export default getListMenuUserSlice.reducer;
